import { useMemo, useState } from 'react'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'
import collectionsEmptyStateImage from '~/Images/collections-empty-state.png'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
} from '~/client/components/ui/pagination'

const COLLECTIONS_PAGE_SIZE = 10
const PURPLE = '#6B21E8'

export type HomeCollection = {
  id: number | string
  name?: string | null
  slug: string
  venueGroupName?: string | null
  ownerName?: string | null
  lastUpdatedAt?: string | null
}

type CollectionsHomeTableProps = {
  collections: HomeCollection[]
  isLoading: boolean
  onCreateCollection: () => void
  onOpenCollection: (slug: string) => void
}

export function CollectionsHomeTable({
  collections,
  isLoading,
  onCreateCollection,
  onOpenCollection,
}: CollectionsHomeTableProps) {
  const [hoveredRow, setHoveredRow] = useState<number | string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = Math.max(1, Math.ceil(collections.length / COLLECTIONS_PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedCollections = collections.slice(
    (safePage - 1) * COLLECTIONS_PAGE_SIZE,
    safePage * COLLECTIONS_PAGE_SIZE,
  )
  const pageList = useMemo(() => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 7) {
      for (let p = 1; p <= totalPages; p++) pages.push(p)
      return pages
    }
    pages.push(1)
    const left = Math.max(2, safePage - 1)
    const right = Math.min(totalPages - 1, safePage + 1)
    if (left > 2) pages.push('ellipsis')
    for (let p = left; p <= right; p++) pages.push(p)
    if (right < totalPages - 1) pages.push('ellipsis')
    pages.push(totalPages)
    return pages
  }, [safePage, totalPages])

  return (
    <div
      className="mt-8 pb-20"
      style={{ animation: 'recs-fadeUp 0.4s ease 0.15s both' }}
    >
      <div className="mb-[18px] flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">Collections</h2>
      </div>

      {isLoading ? (
        <div className="overflow-hidden rounded-[14px] border border-[#E9E8E3] bg-white">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-6 border-b border-[#F3F3F0] px-5 py-4">
              <div
                className="h-4 rounded"
                style={{
                  width: 160,
                  animation: `recs-barShimmer 1.8s ease-in-out ${i * 0.15}s infinite`,
                  background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
                  backgroundSize: '400px 100%',
                }}
              />
              <div className="h-3 rounded bg-gray-100" style={{ width: 90 }} />
              <div className="h-3 rounded bg-gray-100" style={{ width: 110 }} />
              <div className="h-3 rounded bg-gray-100" style={{ width: 70 }} />
            </div>
          ))}
        </div>
      ) : collections.length === 0 ? (
        <div className="rounded-[14px] border border-[#E9E8E3] bg-white px-8 py-14 text-center">
          <img src={collectionsEmptyStateImage} alt="" className="mx-auto mb-5 h-24 w-auto" />
          <div className="mb-1 text-sm font-bold text-gray-900">No collections yet</div>
          <div className="mb-5 text-[13px] text-gray-400">Start a shareable list of venues</div>
          <button
            data-testid="create-collection-button"
            onClick={onCreateCollection}
            className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#7c3aed] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9]"
          >
            + Create Collection
          </button>
        </div>
      ) : (
        <div
          className="flex flex-col overflow-hidden rounded-[14px] border border-[#E9E8E3] bg-white"
          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[#E9E8E3] bg-[#F9F8FF]">
                {['Collection Name', 'City', 'Owner', 'Last Updated', ''].map((h, i) => (
                  <th
                    key={i}
                    className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedCollections.map((collection, i) => {
                const isLast = i === paginatedCollections.length - 1
                const hovered = hoveredRow === collection.id
                return (
                  <tr
                    key={collection.id}
                    data-testid="collection-list-item"
                    className="cursor-pointer"
                    onMouseEnter={() => setHoveredRow(collection.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => onOpenCollection(collection.slug)}
                  >
                    <td
                      className="px-5 text-sm font-semibold text-gray-900 transition-colors"
                      style={{ height: 52, borderBottom: isLast ? 'none' : '1px solid #F3F3F0', background: hovered ? '#F9F8FF' : '#fff' }}
                    >
                      {collection.name || 'Untitled Collection'}
                    </td>
                    <td
                      className="whitespace-nowrap px-5 text-[13px] text-gray-500 transition-colors"
                      style={{ height: 52, borderBottom: isLast ? 'none' : '1px solid #F3F3F0', background: hovered ? '#F9F8FF' : '#fff' }}
                    >
                      {collection.venueGroupName || '—'}
                    </td>
                    <td
                      className="whitespace-nowrap px-5 text-[13px] text-gray-500 transition-colors"
                      style={{ height: 52, borderBottom: isLast ? 'none' : '1px solid #F3F3F0', background: hovered ? '#F9F8FF' : '#fff' }}
                    >
                      {collection.ownerName || '—'}
                    </td>
                    <td
                      className="whitespace-nowrap px-5 text-[13px] text-gray-500 transition-colors"
                      style={{ height: 52, borderBottom: isLast ? 'none' : '1px solid #F3F3F0', background: hovered ? '#F9F8FF' : '#fff' }}
                    >
                      {collection.lastUpdatedAt ? (formatUtcDateLongFromUnknown(collection.lastUpdatedAt, 'en-US') ?? '—') : '—'}
                    </td>
                    <td
                      className="px-4 text-right transition-colors"
                      style={{ height: 52, borderBottom: isLast ? 'none' : '1px solid #F3F3F0', background: hovered ? '#F9F8FF' : '#fff' }}
                    >
                      <span className="text-base transition-colors" style={{ color: hovered ? PURPLE : '#D1D5DB' }}>→</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-[#F3F3F0] bg-[#FAFAF4] px-5 py-3">
            <span className="text-xs text-gray-400">
              Showing {paginatedCollections.length} of {collections.length} collections
            </span>
            {totalPages > 1 && (
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  {pageList.map((p, i) => (
                    <PaginationItem key={`${p}-${i}`}>
                      {p === 'ellipsis' ? (
                        <PaginationEllipsis />
                      ) : (
                        <PaginationLink
                          href="#"
                          isActive={p === safePage}
                          onClick={(e) => {
                            e.preventDefault()
                            if (p !== safePage) setCurrentPage(p)
                          }}
                        >
                          {p}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      aria-disabled={safePage === totalPages}
                      className={safePage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      onClick={(e) => {
                        e.preventDefault()
                        if (safePage < totalPages) setCurrentPage(safePage + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
